import { MdBedroomParent } from "react-icons/md";
import { defineType } from "sanity";
import suiteCategory from "./suiteCategory";
import suiteClassGroup from "./suiteClassGroup";
import { multilingualStringPreview } from "../base/multilingualArrayString";
import suiteClassGenericFeature from "./suiteClassGenericFeature";

export default defineType({
  name: "suiteClass",
  title: "Suite Class",
  type: "document",
  icon: MdBedroomParent,
  orderings: [
    {
      title: "By Valid From",
      name: "validFromAsc",
      by: [{ field: "validFrom", direction: "asc" }]
    },
    {
      title: "Custom ordering",
      name: "orderAsc",
      by: [{ field: "order", direction: "asc" }]
    }
  ],
  preview: {
    select: {
      title: "name",
      validFrom: "validFrom",
      order: "order",
      classicImages: "classicImages",
      expeditionImages: "expeditionImages"
    },
    prepare: ({
      title,
      classicImages,
      expeditionImages,
      validFrom,
      order
    }) => ({
      title: multilingualStringPreview(title),
      subtitle: `Valid From: ${new Date(validFrom).toLocaleDateString("en-GB")} - Order: ${order}`,
      media: (classicImages ?? [])[0] ?? (expeditionImages ?? [])[0]
    })
  },
  groups: [
    { name: "common", title: "Common", default: true },
    { name: "classic", title: "Classic" },
    { name: "expedition", title: "Expedition" }
  ],
  fields: [
    {
      name: "suiteClassGroup",
      title: "Suite Class Group",
      group: "common",
      type: "reference",
      to: [{ type: suiteClassGroup.name }],
      validation: (Rule) => Rule.required()
    },
    {
      name: "validFrom",
      title: "Valid From",
      group: "common",
      type: "date",
      validation: (Rule) => Rule.required()
    },
    {
      name: "name",
      title: "Name",
      group: "common",
      type: "multilingualArrayString",
      validation: (Rule) => Rule.required()
    },
    {
      name: "subtitle",
      title: "Subtitle",
      group: "common",
      type: "multilingualArrayString",
      validation: (Rule) => Rule.required()
    },
    {
      name: "order",
      title: "Order",
      group: "common",
      type: "number",
      validation: (Rule) => Rule.required()
    },
    {
      name: "suiteCategories",
      title: "Suite Categories",
      group: "common",
      type: "array",
      of: [
        {
          name: "suiteCategory",
          title: "Suite Category",
          type: "reference",
          to: [{ type: suiteCategory.name }]
        }
      ]
    },
    {
      name: "genericFeatureItems",
      title: "Generic Feature Items",
      group: "common",
      type: "array",
      of: [
        {
          name: suiteClassGenericFeature.name,
          title: "Generic Feature Item",
          type: "reference",
          to: [{ type: suiteClassGenericFeature.name }]
        }
      ]
    },
    {
      name: "classicImages",
      title: "Classic Images",
      group: "classic",
      type: "array",
      of: [{ type: "imageWithCaption" }]
    },
    {
      name: "expeditionImages",
      title: "Expedition Images",
      group: "expedition",
      type: "array",
      of: [{ type: "imageWithCaption" }]
    },
    {
      name: "classicDescription",
      title: "Classic Description",
      group: "classic",
      type: "multilingualArrayBlockContent",
      validation: (Rule) => Rule.required()
    },
    {
      name: "expeditionDescription",
      title: "Expedition Description",
      group: "expedition",
      type: "multilingualArrayBlockContent",
      validation: (Rule) => Rule.required()
    },
    {
      name: "classicInclusions",
      title: "Classic Inclusions",
      group: "classic",
      type: "array",
      of: [{ type: "inclusionItem" }]
    },
    {
      name: "expeditionInclusions",
      title: "Expedition Inclusions",
      group: "expedition",
      type: "array",
      of: [{ type: "inclusionItem" }]
    },
    {
      name: "classicPerks",
      title: "Classic Perks",
      group: "classic",
      type: "array",
      of: [{ type: "perk" }]
    },
    {
      name: "expeditionPerks",
      title: "Expedition Perks",
      group: "expedition",
      type: "array",
      of: [{ type: "perk" }]
    }
  ]
});
